// src/gestion-logistics/components/ShipmentFormModal.tsx
import React, { useEffect, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
  Divider,
  Grid,
  Button,
  Card,
  Typography,
  Alert,
  message,
} from "antd";
import {
  FileTextOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CarOutlined,
  ContainerOutlined,
  CalendarOutlined,
  InboxOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useShipments } from "../hooks/useShipments";
import useShipmentWarehouse from "../hooks/useShipmentWarehouse";
import { useSelection } from "../../SelectionContext";
import {
  Shipment,
  ShipmentFormData,
  ShipmentStatus,
  TransportMode,
  Container,
  Package,
} from "../types/shipment.types";
import ClientSelector from "./ClientSelector";
import CountrySelector from "./CountrySelector";
import ContainerManager from "./ContainerManager";
import PackageManager from "./PackageManager";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

interface ShipmentFormModalProps {
  visible: boolean;
  onClose: () => void;
  shipment?: Shipment | null;
  mode: "create" | "edit";
}

/**
 * Modal de formulaire pour créer ou éditer un dossier d'expédition
 */
const ShipmentFormModal: React.FC<ShipmentFormModalProps> = ({
  visible,
  onClose,
  shipment,
  mode,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const [form] = Form.useForm();
  const { createShipment, updateShipment } = useShipments();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const warehouse = useShipmentWarehouse({ open: visible, shipment: mode === "edit" ? shipment : null,
    companyId: selectedCompany, selectedWarehouse });
  const submitting = useRef(false);
  const [loading, setLoading] = React.useState(false);
  const [containers, setContainers] = React.useState<Container[]>([]);
  const [packages, setPackages] = React.useState<Package[]>([]);
  const [transportMode, setTransportMode] = React.useState<TransportMode>(
    TransportMode.SEA
  );
  const watchedValues = Form.useWatch([], form) || {};
  const draft = useRef("");
  draft.current = JSON.stringify([watchedValues, containers, packages]);

  const transportModeLabels: Record<TransportMode, string> = {
    [TransportMode.SEA]: "Transport maritime",
    [TransportMode.AIR]: "Transport aerien",
    [TransportMode.ROAD]: "Transport routier",
    [TransportMode.RAIL]: "Transport ferroviaire",
    [TransportMode.MULTIMODAL]: "Transport multimodal",
  };

  const sectionItems = [
    { key: "general", label: "Informations Generales", icon: <FileTextOutlined /> },
    { key: "parties", label: "Parties", icon: <TeamOutlined /> },
    { key: "route", label: "Itineraire", icon: <EnvironmentOutlined /> },
    { key: "transport", label: "Details Transport", icon: <CarOutlined /> },
    { key: "goods", label: "Marchandise", icon: <ContainerOutlined /> },
    { key: "containers", label: "Conteneurs", icon: <InboxOutlined /> },
    { key: "packages", label: "Colis", icon: <ContainerOutlined /> },
    { key: "dates", label: "Dates", icon: <CalendarOutlined /> },
    { key: "notes", label: "Notes", icon: <FileTextOutlined /> },
  ];

  const scrollToSection = (sectionKey: string) => {
    document
      .getElementById(`shipment-form-${sectionKey}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const formatLocation = (city?: string, country?: string) => {
    if (city && country) return `${city} (${country})`;
    return city || country || "";
  };

  const formatRoute = () => {
    const origin = formatLocation(
      watchedValues.origin_city,
      watchedValues.origin_country
    );
    const destination = formatLocation(
      watchedValues.destination_city,
      watchedValues.destination_country
    );

    if (!origin && !destination) return "Itineraire a completer";
    if (origin && destination) return `${origin} -> ${destination}`;
    return origin || destination;
  };

  const SectionTitle = ({
    id,
    icon,
    title,
  }: {
    id: string;
    icon: React.ReactNode;
    title: string;
  }) => (
    <div id={`shipment-form-${id}`}>
      <Divider orientation="left">
        {icon} {title}
      </Divider>
    </div>
  );

  // Réinitialiser le formulaire quand le modal s'ouvre/ferme
  useEffect(() => {
    if (visible && shipment && mode === "edit") {
      form.setFieldsValue({
        ...shipment,
        booking_date: shipment.booking_date ? dayjs(shipment.booking_date) : null,
        etd: shipment.etd ? dayjs(shipment.etd) : null,
        eta: shipment.eta ? dayjs(shipment.eta) : null,
        actual_departure_date: shipment.actual_departure_date
          ? dayjs(shipment.actual_departure_date)
          : null,
        actual_arrival_date: shipment.actual_arrival_date
          ? dayjs(shipment.actual_arrival_date)
          : null,
      });
      setTransportMode(shipment.transport_mode);
      setContainers(shipment.containers || []);
      setPackages(shipment.packages || []);
    } else if (visible && mode === "create") {
      form.resetFields();
      form.setFieldsValue({
        status: ShipmentStatus.DRAFT,
        transport_mode: TransportMode.SEA,
      });
      setTransportMode(TransportMode.SEA);
      setContainers([]);
      setPackages([]);
    }
  }, [visible, shipment, mode, form]);

  const handleSubmit = async () => {
    if (submitting.current) return;
    submitting.current = true;
    const isCurrent = warehouse.captureContext();
    try {
      const values = await form.validateFields();
      setLoading(true);
      const submittedDraft = draft.current;
      const warehouseFields = await warehouse.verify();
      if (draft.current !== submittedDraft) throw new Error("La saisie a changé pendant la vérification. Vérifiez puis réessayez.");

      // Formater les dates pour MySQL
      const formData: any = {
        ...values,
        ...warehouseFields,
        booking_date: values.booking_date
          ? values.booking_date.format("YYYY-MM-DD")
          : null,
        etd: values.etd ? values.etd.format("YYYY-MM-DD") : null,
        eta: values.eta ? values.eta.format("YYYY-MM-DD") : null,
        actual_departure_date: values.actual_departure_date
          ? values.actual_departure_date.format("YYYY-MM-DD")
          : null,
        actual_arrival_date: values.actual_arrival_date
          ? values.actual_arrival_date.format("YYYY-MM-DD")
          : null,
        // NOUVEAU: Ajouter conteneurs et colis
        containers: transportMode === TransportMode.SEA ? containers : undefined,
        packages: packages.length > 0 ? packages : undefined,
      };
      if (mode === "edit") delete formData.warehouse_id;

      let success = false;

      if (mode === "create") {
        const result = await createShipment(formData);
        success = result !== null;
      } else if (shipment) {
        success = await updateShipment(shipment.id, formData);
      }

      if (success) {
        await warehouse.saved();
        if (!isCurrent()) return;
        form.resetFields();
        onClose();
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error("[LOGISTICS] Form validation failed:", error);
      if (error instanceof Error) message.error(error.message);
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  const handleClientChange = (clientId: number, clientName: string) => {
    form.setFieldsValue({
      client_id: clientId,
      client_name: clientName,
    });
  };

  return (
    <Modal
      title={
        <span>
          <FileTextOutlined style={{ marginRight: 8 }} />
          {mode === "create" ? "Nouveau Dossier" : "Modifier le Dossier"}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      className={isMobile ? "shipment-form-modal-mobile" : undefined}
      centered={!isMobile}
      width={isMobile ? "100vw" : 1080}
      style={isMobile ? { top: 0, paddingBottom: 0, margin: 0, maxWidth: "100vw" } : undefined}
      styles={
        isMobile
          ? {
              content: { borderRadius: 0 },
              body: {
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
                padding: 12,
              },
            }
          : {
              body: {
                maxHeight: "calc(100vh - 200px)",
                overflowY: "auto",
              },
            }
      }
      okText={mode === "create" ? "Créer" : "Enregistrer"}
      cancelText="Annuler"
      destroyOnClose
    >
      {warehouse.error && <Alert type="error" showIcon message={warehouse.error}
        action={<Button onClick={() => { void warehouse.refresh().catch(() => {}); }}>Réessayer</Button>} style={{ marginBottom: 16 }} />}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: ShipmentStatus.DRAFT,
          transport_mode: TransportMode.SEA,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "220px minmax(0, 1fr)",
            gap: isMobile ? 12 : 18,
            alignItems: "start",
          }}
        >
          <div
            style={{
              position: isMobile ? "static" : "sticky",
              top: 0,
              border: "1px solid var(--border-color)",
              borderRadius: 8,
              overflow: "hidden",
              background: "var(--bg-primary)",
            }}
          >
            {sectionItems.map((item) => (
              <Button
                key={item.key}
                type="text"
                block
                icon={item.icon}
                onClick={() => scrollToSection(item.key)}
                style={{
                  height: 44,
                  justifyContent: "flex-start",
                  borderRadius: 0,
                  borderBottom: "1px solid var(--border-color)",
                  color: "var(--text-primary)",
                  fontWeight: 500,
                }}
              >
                {item.label}
              </Button>
            ))}
          </div>

          <div style={{ minWidth: 0 }}>
            <Card
              size="small"
              style={{ marginBottom: 12, borderRadius: 8 }}
              bodyStyle={{ padding: isMobile ? 12 : 16 }}
            >
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12}>
                  <Text type="secondary">Reference dossier</Text>
                  <div style={{ fontWeight: 600 }}>
                    {mode === "edit" && shipment?.shipment_number
                      ? shipment.shipment_number
                      : "Generee automatiquement"}
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary">Client</Text>
                  <div style={{ fontWeight: 600 }}>
                    {watchedValues.client_name || "Client a selectionner"}
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary">Itineraire</Text>
                  <div style={{ fontWeight: 600 }}>{formatRoute()}</div>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary">Transport</Text>
                  <div style={{ fontWeight: 600 }}>
                    {transportModeLabels[transportMode] || "-"}
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary">Marchandise</Text>
                  <div style={{ fontWeight: 600 }}>
                    {watchedValues.goods_description || "Description a completer"}
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary">Conteneurs & colis</Text>
                  <div style={{ fontWeight: 600 }}>
                    {containers.length} conteneur(s) - {packages.length} colis
                  </div>
                </Col>
              </Row>
            </Card>
            {/* SECTION 1: Informations Générales */}
            <SectionTitle
              id="general"
              icon={<FileTextOutlined />}
              title="Informations Generales"
            />

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Mode de Transport"
              name="transport_mode"
              rules={[{ required: true, message: "Le mode de transport est requis" }]}
            >
              <Select
                onChange={(value) => setTransportMode(value as TransportMode)}
                placeholder="Sélectionner le mode de transport"
              >
                <Option value={TransportMode.SEA}>Maritime</Option>
                <Option value={TransportMode.AIR}>Aérien</Option>
                <Option value={TransportMode.ROAD}>Routier</Option>
                <Option value={TransportMode.RAIL}>Ferroviaire</Option>
                <Option value={TransportMode.MULTIMODAL}>Multimodal</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label="Statut" name="status" rules={[{ required: true }]}>
              <Select placeholder="Sélectionner le statut">
                <Option value={ShipmentStatus.DRAFT}>Brouillon</Option>
                <Option value={ShipmentStatus.CONFIRMED}>Confirmé</Option>
                <Option value={ShipmentStatus.IN_TRANSIT}>En Transit</Option>
                <Option value={ShipmentStatus.DELIVERED}>Livré</Option>
                <Option value={ShipmentStatus.COMPLETED}>Terminé</Option>
                <Option value={ShipmentStatus.CANCELLED}>Annulé</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Document de Transport"
              name="reference_client"
              tooltip="BL (Bill of Lading) pour maritime, LTA (Lettre de Transport Aérien) pour aérien, CMR pour routier"
            >
              <Input placeholder="Ex: BL-2025-001, LTA-2025-001, CMR-2025-001" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label="Type de Service" name="service_type">
              <Input placeholder="Ex: FCL, LCL, Express" />
            </Form.Item>
          </Col>
        </Row>

        {/* SECTION 2: Parties (Client, Consignataire, Expéditeur, Destinataire) */}
        <span id="shipment-form-parties" />
        <Divider orientation="left">
          <UserOutlined /> Parties
        </Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Client" name="client_id">
              <ClientSelector
                onChange={handleClientChange}
                companyId={selectedCompany}
                warehouseId={selectedWarehouse}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              label="Nom du Client"
              name="client_name"
              rules={[{ required: true, message: "Le nom du client est requis" }]}
              tooltip="Ce champ est automatiquement rempli lors de la sélection du client"
            >
              <Input
                placeholder="Sélectionnez un client pour voir son nom ici"
                disabled
                style={{ cursor: 'not-allowed', backgroundColor: '#f5f5f5' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Consignataire" name="consignee_name" tooltip="Destinataire des marchandises">
              <Input placeholder="Nom du consignataire" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label="Expéditeur" name="shipper_name" tooltip="Origine de l'envoi">
              <Input placeholder="Nom de l'expéditeur" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Destinataire Final" name="receiver_name" tooltip="Réceptionnaire final des marchandises">
              <Input placeholder="Nom du destinataire" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label="Adresse du Destinataire" name="receiver_address">
              <Input placeholder="Adresse complète de livraison" />
            </Form.Item>
          </Col>
        </Row>

        <span id="shipment-form-route" />
        {/* SECTION 3: Itinéraire */}
        <Divider orientation="left">
          <EnvironmentOutlined /> Itinéraire
        </Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Pays d'Origine" name="origin_country">
              <CountrySelector placeholder="Sélectionner le pays d'origine" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label="Ville d'Origine" name="origin_city">
              <Input placeholder="Ex: Paris" />
            </Form.Item>
          </Col>
        </Row>

        {transportMode === TransportMode.SEA && (
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Port de Chargement" name="port_of_loading" tooltip="Port d'embarquement des marchandises">
                <Input placeholder="Ex: Port de Shanghai" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="Port de Déchargement" name="port_of_discharge" tooltip="Port de destination">
                <Input placeholder="Ex: Port de Cotonou" />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Pays de Destination" name="destination_country">
              <CountrySelector placeholder="Sélectionner le pays de destination" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label="Ville de Destination" name="destination_city">
              <Input placeholder="Ex: New York" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item label="Destination Finale" name="final_destination" tooltip="Adresse ou lieu de livraison final si différent de la ville de destination">
              <Input placeholder="Ex: Entrepôt XYZ, Zone Industrielle..." />
            </Form.Item>
          </Col>
        </Row>

        <span id="shipment-form-transport" />
        {/* SECTION 4: Détails Transport */}
        <Divider orientation="left">
          <CarOutlined /> Détails Transport
        </Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Incoterm" name="incoterm" tooltip="Termes commerciaux internationaux">
              <Select placeholder="Sélectionner un Incoterm" allowClear>
                <Option value="EXW">EXW - Ex Works</Option>
                <Option value="FCA">FCA - Free Carrier</Option>
                <Option value="CPT">CPT - Carriage Paid To</Option>
                <Option value="CIP">CIP - Carriage Insurance Paid</Option>
                <Option value="DAP">DAP - Delivered At Place</Option>
                <Option value="DPU">DPU - Delivered Place Unloaded</Option>
                <Option value="DDP">DDP - Delivered Duty Paid</Option>
                <Option value="FAS">FAS - Free Alongside Ship</Option>
                <Option value="FOB">FOB - Free On Board</Option>
                <Option value="CFR">CFR - Cost and Freight</Option>
                <Option value="CIF">CIF - Cost Insurance Freight</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label="N° de Connaissement (BL)" name="bl_number" tooltip="Bill of Lading pour maritime">
              <Input placeholder="Ex: BL123456789" />
            </Form.Item>
          </Col>
        </Row>

        {/* Champs spécifiques au transport maritime */}
        {transportMode === TransportMode.SEA && (
          <>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label="Compagnie Maritime" name="carrier_name">
                  <Input placeholder="Ex: CMA CGM, MSC, MAERSK" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Nom du Navire" name="vessel_name">
                  <Input placeholder="Nom du navire" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label="Numéro de Voyage" name="voyage_number">
                  <Input placeholder="Ex: 123N" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Transitaire" name="clearing_agent_name">
                  <Input placeholder="Nom du transitaire en douane" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* Champs spécifiques au transport aérien */}
        {transportMode === TransportMode.AIR && (
          <>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label="Compagnie Aérienne" name="carrier_name">
                  <Input placeholder="Ex: Air France, Emirates" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Numéro de Vol" name="voyage_number">
                  <Input placeholder="Ex: AF123" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <span id="shipment-form-goods" />
        {/* SECTION 5: Marchandise */}
        <Divider orientation="left">
          <ContainerOutlined /> Marchandise
        </Divider>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item label="Description" name="goods_description">
              <TextArea
                rows={2}
                placeholder="Description de la marchandise"
                maxLength={500}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item label="Poids Total (kg)" name="total_weight">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item label="Volume (m³)" name="total_volume">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item label="Nombre de Colis" name="number_of_packages">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                placeholder="0"
              />
            </Form.Item>
          </Col>
        </Row>

        <span id="shipment-form-containers" />
        {/* SECTION 6: Conteneurs et Colis (conditionnels) */}
        {transportMode === TransportMode.SEA && (
          <>
            <Divider orientation="left">
              <ContainerOutlined /> Conteneurs
            </Divider>
            <ContainerManager
              containers={containers}
              onChange={setContainers}
              disabled={loading}
            />
          </>
        )}

        <span id="shipment-form-packages" />
        {/* Section Colis (pour groupage) */}
        {packages.length > 0 || transportMode === TransportMode.SEA ? (
          <>
            <Divider orientation="left" style={{ marginTop: 24 }}>
              Colis (optionnel - pour groupage)
            </Divider>
            <PackageManager
              packages={packages}
              containers={containers}
              onChange={setPackages}
              disabled={loading}
              showContainerLink={transportMode === TransportMode.SEA}
            />
          </>
        ) : null}

        <span id="shipment-form-dates" />
        {/* SECTION 7: Dates */}
        <Divider orientation="left">
          <CalendarOutlined /> Dates
        </Divider>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item label="Date de Réservation" name="booking_date">
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="JJ/MM/AAAA"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label="ETD (Départ Prévu)"
              name="etd"
              tooltip="Estimated Time of Departure"
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="JJ/MM/AAAA"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item
              label="ETA (Arrivée Prévue)"
              name="eta"
              tooltip="Estimated Time of Arrival"
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="JJ/MM/AAAA"
              />
            </Form.Item>
          </Col>
        </Row>

        <span id="shipment-form-notes" />
        {/* SECTION 7: Notes */}
        <Divider orientation="left">Notes Internes</Divider>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item label="Notes" name="internal_notes">
              <TextArea
                rows={3}
                placeholder="Notes internes (non visibles par le client)"
                maxLength={1000}
              />
            </Form.Item>
          </Col>
        </Row>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default ShipmentFormModal;
